import { JwtHighbondClient } from '@acl-services/jwt-highbond-client';
import { ApiErrorRecovery } from '@issuemanager/api-client';
import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { performOidcSilentReauth } from '../../services/oidc/silent-reauth';

const toHeaderRecord = (headers: Headers): Record<string, string> => {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    record[key] = value;
  });
  return record;
};

const toAxiosResponse = async (
  response: Response,
  config: InternalAxiosRequestConfig,
): Promise<AxiosResponse> => ({
  data: await response.json().catch(() => null),
  status: response.status,
  statusText: response.statusText,
  headers: toHeaderRecord(response.headers),
  config,
  request: config,
});

let reauthInFlight: Promise<void> | null = null;

const performOidcReauth = (resourceIndicator: string): Promise<void> => {
  if (!reauthInFlight) {
    reauthInFlight = performOidcSilentReauth(resourceIndicator).finally(() => {
      reauthInFlight = null;
    });
  }
  return reauthInFlight;
};

const isDirectApiRequest = ({
  directApiUrl,
  request,
}: {
  request: Pick<InternalAxiosRequestConfig, 'baseURL' | 'url'> | undefined;
  directApiUrl: string;
}): boolean => {
  const { baseURL = '', url = '' } = request ?? {};
  return `${baseURL}${url}`.startsWith(directApiUrl);
};

const createOidcRecovery =
  (resourceIndicator: string): ApiErrorRecovery =>
  async ({ error, reissueRequest }) => {
    if (error.response?.status !== 401) {
      return null;
    }
    await performOidcReauth(resourceIndicator);
    return reissueRequest();
  };

const createHighbondRecovery =
  (highbondClient: JwtHighbondClient): ApiErrorRecovery =>
  async ({ error }) => {
    const request = error.config;
    if (!request) {
      return null;
    }

    const response = await highbondClient.fetchWithTokenRefresh(
      request.url ?? '',
      {
        method: request.method?.toUpperCase(),
        headers: request.headers,
        body: request.data,
        credentials: 'include',
      },
    );
    if (!response.ok) {
      return null;
    }

    return toAxiosResponse(response, request);
  };

type ApiErrorRecoveryConfig = {
  highbondClient: JwtHighbondClient;
  oidcResourceIndicator: string | undefined;
  directApiUrl: string;
};

export const createApiErrorRecovery = ({
  highbondClient,
  oidcResourceIndicator,
  directApiUrl,
}: ApiErrorRecoveryConfig): ApiErrorRecovery => {
  if (!oidcResourceIndicator) {
    return createHighbondRecovery(highbondClient);
  }

  const oidcRecovery = createOidcRecovery(oidcResourceIndicator);
  const highbondRecovery = createHighbondRecovery(highbondClient);

  // direct api call means its a legacy app like drive
  // that hasn't migrated to oidc so we need highbond cookie refresh
  return (context) =>
    isDirectApiRequest({ request: context.error.config, directApiUrl })
      ? highbondRecovery(context)
      : oidcRecovery(context);
};
